import _ from "lodash";
import React, { useEffect, useState } from "react";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import UIButton from "UIComponents/UIButton";
import classes from "./TagCell.scss";
import { colors } from "@toddle-edu/ds-theme";
import { useI18n } from "Hooks";
import AddItemsModal from "../AddItemsModal/AddItemsModal";
import { createMultiLevelTags } from "NodeEditor/Modules/NodeEditorModule";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";

const plusBtnStyle = { color: colors.neutral300, cursor: "pointer" };
const addTagBtnStyle = {
  width: "36px",
  height: "20px",
  borderRadius: "12px",
  padding: "4px 8px",
  minWidth: "36px",
  marginLeft: "auto",
};
const addTagLabelStyle = { fontSize: "1rem" };

const TagCell = props => {
  const {
    tags,
    organizationTags,
    mode,
    createMultiLevelTags,
    inputForMultiLevelTagsQuery,
    organizationTagsObjectMap,
    onAddTags,
  } = props;
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n(["common", "scopeAndSequence"]);

  //exisiting tag ids
  const tagIds = _.map(tags, tag => {
    return tag.id;
  });

  const getSelectedTags = () => {
    const tagList = [];
    _.forEach(tags, ({ id }) => {
      const tag = organizationTagsObjectMap[id];
      if (tag) tagList.push(tag);
    });
    return tagList;
  };

  //selected tags contains the final result of selected tags from add tags modal
  const [selectedTags, setSelectedTags] = useState(getSelectedTags());
  useEffect(() => {
    setSelectedTags(getSelectedTags());
  }, [tags]);

  //list of existing tag labels. Labels and ids are got from separate lists to manage edit state
  const tagLabelAndIcons = _.map(tagIds, tagId => {
    const label = _.get(organizationTagsObjectMap[tagId], "label", "");
    const iconURL = _.get(organizationTagsObjectMap[tagId], "icon.url", "");
    return { label, iconURL };
  });
  //max count contains maximum number of tags to display. after that we show more with tooltip
  const maxCount = 2;

  const tooltipComp = (
    <div>
      {_.map(_.slice(tagLabelAndIcons, maxCount, Infinity), ({ label }, i) => {
        return (
          <React.Fragment key={i}>
            {label}
            <br />
          </React.Fragment>
        );
      })}
    </div>
  );

  const toggleAddTagsModal = () => {
    if (isAddTagsModalOpen) setSelectedTags([]);
    else setSelectedTags(getSelectedTags());
    setIsAddTagsModalOpen(prevState => !prevState);
  };

  //this function creates a new tag when user clicks on plus icon in add tags modal
  const onCreateNewTag = async value => {
    const input = [{ label: value }];
    setIsLoading(true);
    const addedTag = await createMultiLevelTags({
      input,
      inputForMultiLevelTagsQuery,
    });
    // The Dnd component needs the value property to render the label of tag
    const newTag = {
      ...addedTag,
      value,
    };

    setIsLoading(false);
    if (newTag) {
      setSelectedTags(prevTags => [...prevTags, newTag]);
    }
  };

  const handleAddTags = () => {
    onAddTags({ itemsList: selectedTags });
    toggleAddTagsModal();
  };

  //localized texts
  const texts = {
    addText: t("common:edit"),
    buttonText1: t("common:cancel"),
    buttonText2: t("common:save"),
    modalTitle: t("common:edit_with_label", { label: t("common:tags") }),
    searchPlaceholderText: t("scopeAndSequence:enter_a_item", {
      label: t("scopeAndSequence:tag_name"),
    }),
    createText: t("scopeAndSequence:create_new_tag"),
    userNote: t(
      "scopeAndSequence:tag_note_adding_here_will_add_to_all_standards"
    ),
  };

  const getLabelComp = ({ label, index }) => {
    const tagContainerClasses = classNames(classes.tag, classes.tagLabel);
    return (
      <LinkWithTooltip
        tooltip={label}
        linkStyles={plusBtnStyle}
        placement={"right"}
        tabIndex={0}
        trigger={["hover", "focus"]}
      >
        <div key={index} className={tagContainerClasses}>
          <span className={classes.label}>{label}</span>
        </div>
      </LinkWithTooltip>
    );
  };

  const moreTagsCount = `+${_.size(tagLabelAndIcons) - maxCount}`;
  return (
    <div className={classes.tagsContainer}>
      {_.isEmpty(tagLabelAndIcons) && <div>{"--"}</div>}
      {_.map(
        _.slice(tagLabelAndIcons, 0, maxCount),
        ({ label, iconURL }, idx) => getLabelComp({ label, iconURL, idx })
      )}
      {_.size(tagLabelAndIcons) > maxCount && (
        <div className={classes.tag}>
          <LinkWithTooltip
            tooltip={tooltipComp}
            trigger={["click"]}
            placement={"right"}
            linkStyles={plusBtnStyle}
          >
            {moreTagsCount}
          </LinkWithTooltip>
        </div>
      )}
      {mode === "edit" && (
        <UIButton
          className={classes.addTagBtn}
          size="sm"
          type="hollow"
          containerStyle={addTagBtnStyle}
          labelStyle={addTagLabelStyle}
          color="blue"
          onClick={toggleAddTagsModal}
        >
          {texts.addText}
        </UIButton>
      )}
      {isAddTagsModalOpen && (
        <AddItemsModal
          items={organizationTags}
          onClickButton1={toggleAddTagsModal}
          onClickButton2={handleAddTags}
          buttonText1={texts.buttonText1}
          buttonText2={texts.buttonText2}
          modalTitle={texts.modalTitle}
          searchPlaceholderText={texts.searchPlaceholderText}
          isCreateItems={true}
          onCreateItemClick={onCreateNewTag}
          selectedItemsList={selectedTags}
          setSelectedItemsList={setSelectedTags}
          createText={texts.createText}
          userNote={""}
        />
      )}
      {isLoading && <FullScreenLoader />}
    </div>
  );
};
const mapActionCreators = {
  createMultiLevelTags,
};
export default compose(connect(null, mapActionCreators))(TagCell);
